import { useRef, useState, useLayoutEffect } from 'react'

/**
 * Hook that measures the height of a container element using ResizeObserver.
 * Returns a ref to attach to the container and the measured height in pixels.
 *
 * This is needed because ClientDataTable's SimpleBar doesn't work with
 * percentage heights - it needs explicit pixel values.
 */
export function useContainerHeight<T extends HTMLElement = HTMLDivElement>(): [
  React.RefObject<T | null>,
  number,
] {
  const containerRef = useRef<T | null>(null)
  const [height, setHeight] = useState(0)

  useLayoutEffect(() => {
    const element = containerRef.current
    if (!element) return

    const updateHeight = () => {
      const rect = element.getBoundingClientRect()
      setHeight(rect.height)
    }

    // Initial measurement
    updateHeight()

    // Observe size changes
    const resizeObserver = new ResizeObserver(updateHeight)
    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return [containerRef, height]
}
